import React, { useEffect, useState } from "react";
import { getDataWithFilterUrl } from "../../controllers/APIController";
import {
  Table,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableColumn,
  Button,
  useDisclosure,
  Pagination,
  Card,
  CardBody,
  Input,
  Image,
} from "@nextui-org/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faCheckCircle,
  faExclamationTriangle,
  faChartBar,
  faArrowTrendUp,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import DownloadIcon from "../../Images/Icons/download.svg";
import { useParams, useLocation } from "react-router-dom";

import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import NullData from "../../components/ErrorPage/NullData";
import LoadingPage from "../../components/Loader/LoadingPage";
import AddEvidenceModal from "./AddEvidenceModal";
import EditEvidenceModal from "./EditEvidenceModal";
import { StandardPrimaryButton } from "../../components/StandardButtons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, FileUp } from "lucide-react";

// Evidence type descriptions and configurations
const evidenceTypeConfig = {
  "performance-data": {
    title: "Performance Data",
    description: "Track and manage your ESG performance metrics and KPIs",
    pageDescription: "Browse and manage performance evidence files",
    addButtonText: "Add Performance Data",
    nullDataMessage: "No Performance Data found.",
    searchPlaceholder: "Search performance data...",
    category: "Performance Data",
  },
  "audit-reports": {
    title: "Audit Reports",
    description: "Manage and review audit reports and compliance documentation",
    pageDescription: "Browse and manage audit report files",
    addButtonText: "Add Audit Report",
    nullDataMessage: "No Audit Reports found.",
    searchPlaceholder: "Search audit reports...",
    category: "Audit Reports",
  },
  "stakeholder-feedback": {
    title: "Stakeholder Feedback",
    description:
      "Collect and organize stakeholder feedback and survey responses",
    pageDescription: "Browse and manage stakeholder feedback files",
    addButtonText: "Add Stakeholder Feedback",
    nullDataMessage: "No Stakeholder Feedback found.",
    searchPlaceholder: "Search stakeholder feedback...",
    category: "Stakeholder Feedback",
  },
};

// Compliance document type descriptions and configurations
const complianceTypeConfig = {
  certifications: {
    title: "Certifications",
    description: "Manage ESG certifications and standards documentation",
    pageDescription: "Browse and manage certification documents",
    addButtonText: "Add Certification",
    nullDataMessage: "No Certifications found.",
    searchPlaceholder: "Search certifications...",
    category: "Certifications",
  },
  "code-of-conduct": {
    title: "Code of Conduct",
    description: "Manage corporate code of conduct and ethical guidelines",
    pageDescription: "Browse and manage code of conduct documents",
    addButtonText: "Add Code of Conduct",
    nullDataMessage: "No Code of Conduct documents found.",
    searchPlaceholder: "Search code of conduct...",
    category: "Code of Conduct",
  },
  regulatory: {
    title: "Regulatory Compliance",
    description: "Manage regulatory compliance documents and requirements",
    pageDescription: "Browse and manage regulatory compliance documents",
    addButtonText: "Add Regulatory Document",
    nullDataMessage: "No Regulatory Compliance documents found.",
    searchPlaceholder: "Search regulatory compliance...",
    category: "Regulatory Compliance",
  },
};

const PerformanceData = () => {
  const { _type } = useParams(); // performance-data, audit-reports, stakeholder-feedback, certifications, code-of-conduct, regulatory
  const { pathname } = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onOpenChange: onEditOpenChange,
  } = useDisclosure();
  const [selectedEvidence, setSelectedEvidence] = useState(null);

  const UserLogged = JSON.parse(sessionStorage.getItem("userData"));
  const OrgInfo = UserLogged && UserLogged.user.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + OrgInfo;

  // Determine if this is for evidences or compliance-docs
  const isComplianceDocs = pathname.includes("/compliance-docs/");

  // Get configuration for current type
  const currentConfig = isComplianceDocs
    ? complianceTypeConfig[_type] || complianceTypeConfig["certifications"]
    : evidenceTypeConfig[_type] || evidenceTypeConfig["performance-data"];

  const categoryFilter = `&filters[Category][$eqi]=${currentConfig.category}`;
  const fullFilterUrl = orgFilterUrl + categoryFilter;

  // Real data from Evidence collection
  const [evidenceData, setEvidenceData] = useState([]);
  const queryClient = useQueryClient();

  // Fetch evidence data from the backend with category filter

  const { data: evidenceResponse, isLoading } = useQuery({
    queryKey: ["evidences", fullFilterUrl],
    queryFn: () => getDataWithFilterUrl("evidences", fullFilterUrl),
  });

  useEffect(() => {
    const dataList = evidenceResponse?.data || [];
    setEvidenceData(dataList);
  }, [evidenceResponse]);

  const downloadHandler = (url) => {
    window.open(url);
  };

  const handleEvidenceClick = (evidence) => {
    setSelectedEvidence(evidence);
    onEditOpen();
  };

  // Filter data based on search term
  const filteredData = evidenceData.filter((item) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();

    return (
      item?.attributes?.Name?.toLowerCase().includes(searchLower) ||
      item?.attributes?.Description?.toLowerCase().includes(searchLower) ||
      item?.attributes?.Category?.toLowerCase().includes(searchLower)
    );
  });

  const ItemPerPage = 10;
  const totalPage = Math.ceil(filteredData?.length / ItemPerPage);

  // Statistics
  const totalMetrics = evidenceData.length;
  // Since status fields don't exist in evidence data, we'll show placeholder values
  // These can be updated when status fields are added to the evidence schema
  const onTrackMetrics = 0;
  const exceedingMetrics = 0;
  const needsAttentionMetrics = 0;

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Page Header */}
      <div className="bg-white border-b border-neutral-200 px-8 py-6 shadow-sm">
        <div className="flex items-center justify-between max-w-full">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900">
              {currentConfig.title}
            </h1>
            <p className="text-sm text-neutral-600 mt-1">
              {currentConfig.description}
            </p>
          </div>
          <StandardPrimaryButton
            className="px-6 font-medium"
            startContent={
            <FileUp className="w-[16px] h-[16px] fill-[#ffff]"/>
            }
            onClick={() => onOpen()}
          >
            {currentConfig.addButtonText}
          </StandardPrimaryButton>
        </div>
      </div>

      <BreadCrumbsPage>
        <div className="max-w-7xl mx-auto px-8 py-8">
          <LoadingPage
            isLoading={isLoading}
            loadingText={`Loading ${currentConfig.title.toLowerCase()}...`}
          >
            {/* Performance Overview Section */}
            {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <Card className="border border-neutral-200 shadow-sm">
                <CardBody className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <FontAwesomeIcon icon={faChartBar} className="text-primary-600 text-lg" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600">Total Metrics</p>
                      <p className="text-2xl font-bold text-neutral-900">{totalMetrics}</p>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <Card className="border border-neutral-200 shadow-sm">
                <CardBody className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-success-100 rounded-lg">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-success-600 text-lg" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600">On Track</p>
                      <p className="text-2xl font-bold text-neutral-900">{onTrackMetrics}</p>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <Card className="border border-neutral-200 shadow-sm">
                <CardBody className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-100 rounded-lg">
                      <FontAwesomeIcon icon={faArrowTrendUp} className="text-primary-600 text-lg" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600">Exceeding</p>
                      <p className="text-2xl font-bold text-neutral-900">{exceedingMetrics}</p>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <Card className="border border-neutral-200 shadow-sm">
                <CardBody className="p-4">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-600 text-lg" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600">Needs Attention</p>
                      <p className="text-2xl font-bold text-neutral-900">{needsAttentionMetrics}</p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div> */}

            {/* Evidence Data Table */}
            <div className="bg-white rounded-lg border border-neutral-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-neutral-200">
                <div className="flex items-center justify-between">
                  {/* <div>
                    <h3 className="text-lg font-semibold text-neutral-900">{currentConfig.title}</h3>
                    <p className="text-sm text-neutral-600 mt-1">{currentConfig.pageDescription}</p>
                  </div> */}
                  <div className="w-80">
                    <Input
                      placeholder={currentConfig.searchPlaceholder}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      startContent={
                        <FontAwesomeIcon
                          icon={faSearch}
                          className="text-neutral-400 text-sm"
                        />
                      }
                      size="sm"
                    />
                  </div>
                </div>
              </div>
              <div className="px-6 py-4">
                {filteredData && filteredData?.length > 0 ? (
                  <>
                    <Table
                      removeWrapper
                      aria-label={`${currentConfig.title.toLowerCase()} table`}
                      classNames={{
                        wrapper: "shadow-none",
                        th: "bg-neutral-50 text-neutral-700 font-semibold text-xs border-b border-neutral-200 py-3",
                        td: "border-b border-neutral-100 py-2",
                        tr: "hover:bg-neutral-50 transition-colors",
                      }}
                    >
                      <TableHeader>
                        <TableColumn>Name</TableColumn>
                        <TableColumn>Description</TableColumn>
                        <TableColumn>Category</TableColumn>
                        <TableColumn>Reference To</TableColumn>
                        <TableColumn>Upload Date</TableColumn>
                        <TableColumn>Actions</TableColumn>
                      </TableHeader>
                      <TableBody
                        emptyContent={
                          <NullData title={currentConfig.nullDataMessage} />
                        }
                      >
                        {filteredData &&
                          Array.isArray(filteredData) &&
                          filteredData.map((item, index) => (
                            <TableRow
                              key={item.id}
                              className="cursor-pointer hover:bg-neutral-50 transition-colors"
                              onClick={() => handleEvidenceClick(item)}
                            >
                              <TableCell>
                                <div className="flex gap-1 items-center">
                                  < FileText  className="w-[20px] h-[20px]" />
                                  {item?.attributes?.Name}
                                </div>
                              </TableCell>
                              <TableCell>
                                {item?.attributes?.Description}
                              </TableCell>
                              <TableCell>
                                {item?.attributes?.Category || "---"}
                              </TableCell>
                              <TableCell>
                                {item?.attributes?.ReferenceList?.data?.length >
                                0
                                  ? item?.attributes?.ReferenceList?.data
                                      ?.length
                                  : "---"}
                              </TableCell>
                              <TableCell>
                                {new Date(
                                  item?.attributes?.createdAt
                                ).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                <div
                                  className="flex items-center gap-1 cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadHandler(
                                      item?.attributes?.EvidenceUrl
                                    );
                                  }}
                                >
                                  <Image
                                    width={20}
                                    alt="NextUI hero Image"
                                    src={DownloadIcon}
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                    {filteredData?.length > 0 && (
                      <div className="w-full mt-6 pt-4 border-t border-neutral-200 flex items-center justify-center">
                        <Pagination
                          showControls
                          total={totalPage}
                          color="secondary"
                          page={currentPage}
                          onChange={setCurrentPage}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <NullData />
                )}
              </div>
            </div>
          </LoadingPage>
        </div>
      </BreadCrumbsPage>

      <AddEvidenceModal
        onSubmitHandler={1}
        id={1}
        isOpen={isOpen}
        onOpen={onOpen}
        onOpenChange={onOpenChange}
        fullFilterUrl={fullFilterUrl}
        pageContext={currentConfig.title}
      />

      <EditEvidenceModal
        isOpen={isEditOpen}
        onOpenChange={onEditOpenChange}
        evidenceData={selectedEvidence}
        pageContext={currentConfig.title}
        fullFilterUrl={fullFilterUrl}
      />
    </div>
  );
};

export default PerformanceData;
